import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'react-feather';
import { useRemote } from '../../../Utils/Utils';
import { QUARTER_END } from '../../../Utils/reportingDateRules';
import Button from '../../../components/Buttons/Button';
import TextInput from '../../../components/Input/TextInput';
import Divider from '../../../components/Divider';
import { toastDanger, toastSuccess, toastWarning } from '../../../components/popups/Toast';

/**
 * "Re-derive QFU data" for the fund and scheme admin pages (code-plan/qfu/plan-qfuRederiveEndpoint.md §2.4).
 *
 * Runs the QFU derivation again at a chosen reporting date, for one fund (`fundNumber` given) or
 * every fund of the company. The date is picked with the system date field (`TextInput`
 * `type="date"`, the calendar control behind the fund page's "Fund started date") and checked against a
 * reporting-date rule: a QFU reports against a quarter end, so that is the default rule, and any
 * other day shows the rule's message and disables the button. Pass another `dateRule` (see
 * Utils/reportingDateRules.js) for a report on another calendar.
 *
 * Renders nothing for a tenant without QFU configuration, which is what GET /qfu/config answers
 * with a 404.
 */
/** A heading and one line per issue, instead of everything joined by semicolons. */
const issuesToast = (heading, issues) => (
    <div>
        <div className="font-weight-semi-bold">{heading}</div>
        <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
            {issues.map((issue, i) => <li key={i}>{issue}</li>)}
        </ul>
    </div>
);

/**
 * RemoteV2 rejects with the bare HTTP status, so the server's message never reaches us; say what
 * each status means for this call instead of a blank "failed".
 */
const failureMessage = (status, fundNumber, at) => {
    switch (status) {
        case 400: return `${at} is not a valid reporting date`;
        case 403: return 'You do not have permission to re-derive QFU data';
        case 409: return fundNumber
            ? `${fundNumber} was not found, or this company has no QFU configuration`
            : 'This company has no QFU configuration';
        default: return `Re-derive failed${typeof status === 'number' ? ` (HTTP ${status})` : ''}`;
    }
};

const RederiveQfuButton = ({ fundNumber, dateRule = QUARTER_END }) => {
    const remote = useRemote();
    const [enabled, setEnabled] = useState(false);
    const [date, setDate] = useState(dateRule.defaultValue(new Date()));
    const [running, setRunning] = useState(false);
    const runningRef = useRef(false);
    // Once the user has picked a date, the fetched default must not replace it.
    const touchedRef = useRef(false);

    useEffect(() => {
        let mounted = true;
        remote.get('/qfu/config')
            .then((settings) => { if (mounted) setEnabled(!!settings); })
            .catch(() => { if (mounted) setEnabled(false); });
        // Start from the latest date the fund (or the company) actually has data for, when the rule
        // allows it; otherwise the rule's calendar default stands.
        remote.get(`/qfu/latest-reporting-date${fundNumber ? `?fund=${fundNumber}` : ''}`)
            .then((latest) => {
                if (mounted && !touchedRef.current && latest && latest.date && !dateRule.validate(latest.date)) setDate(latest.date);
            })
            .catch(() => { /* keep the calendar default */ });
        return () => { mounted = false; };
    }, [remote, fundNumber, dateRule]);

    if (!enabled) return null;

    const invalid = dateRule.validate(date);

    async function handleRederive() {
        if (runningRef.current) return;
        if (invalid) { toastDanger(invalid); return; }
        runningRef.current = true;
        setRunning(true);
        const at = dateRule.format ? dateRule.format(date) : date;
        try {
            const path = fundNumber ? `/qfu/derive/${fundNumber}?date=${date}` : `/qfu/derive?date=${date}`;
            const result = await remote.post(path, {});
            if (fundNumber) {
                const issues = result.issues || [];
                const warnings = result.warnings || [];
                if (issues.length > 0) {
                    toastWarning(issuesToast(`${fundNumber} re-derived at ${at}`, [...issues, ...warnings]), { autoClose: 10000 });
                } else if (warnings.length > 0) {
                    toastSuccess(issuesToast(`${fundNumber} re-derived at ${at} (document complete; Disclose files would need)`, warnings), { autoClose: 8000 });
                } else {
                    toastSuccess(`${fundNumber} re-derived at ${at}${result.changed ? '' : ' (no change)'}`);
                }
                // Where the document departs from, or was held to, a legal rule: never blocking, but
                // shown on its own so it is not read as a Disclose gap
                const legislativeNotes = result.legislativeNotes || [];
                if (legislativeNotes.length > 0) {
                    toastWarning(issuesToast(`${fundNumber}: legislative notes`, legislativeNotes), { autoClose: 10000 });
                }
            } else {
                const notes = result.withLegislativeNotes ? `, ${result.withLegislativeNotes} with legislative notes` : '';
                const summary = `${result.funds} funds re-derived at ${at}: ${result.changed} changed, ${result.withIssues} with issues${notes}`;
                if (result.withIssues > 0) toastWarning(summary, { autoClose: 8000 }); else toastSuccess(summary);
            }
        } catch (e) {
            toastDanger(failureMessage(e, fundNumber, at));
        } finally {
            runningRef.current = false;
            setRunning(false);
        }
    }

    return (
        <div className="flex-align-center" style={{ gap: '0.75rem', marginLeft: 'auto' }}>
            <Divider vertical />
            <span className="colour-text-label-grey" style={{ whiteSpace: 'nowrap' }}>Reporting date</span>
            <TextInput
                type="date"
                value={date || ''}
                onChange={(e) => { touchedRef.current = true; setDate(e.target.value || null); }}
                disabled={running}
                invalid={!!invalid}
                title={invalid || `${dateRule.label} dates only`}
                aria-label={`Reporting date (${dateRule.label.toLowerCase()})`}
                style={{ width: '11rem' }}
            />
            {/* No `loading` prop: the shared Button prints a literal "loading" before its label. The
                guard is `disabled` plus the ref; the label changes so the state is still visible. */}
            <Button icon={<RefreshCw />} disabled={running || !!invalid} onClick={handleRederive}>
                {running ? 'Re-deriving…' : fundNumber ? 'Re-derive QFU data' : 'Re-derive all QFU data'}
            </Button>
        </div>
    );
};

export default RederiveQfuButton;
